import { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Calendar,
  Plus,
  Edit2,
  Trash2,
  Lock,
  Unlock,
  ArrowLeft,
  AlertCircle,
  DollarSign,
  Clock,
} from 'lucide-react';
import {
  useAvailabilities,
  useAddAvailability,
  useUpdateAvailability,
  useRemoveAvailability,
  useBlockAvailability,
  useUnblockAvailability,
  useUpdateAvailabilityTimes,
} from '../hooks/useAvailabilities';
import { useApartments } from '../hooks/useApartments';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { AvailabilityCalendarView } from '../components/AvailabilityCalendarView';
import { AvailabilityTimesForm } from '../components/AvailabilityTimesForm';
import { ConflictsPanel } from '../components/ConflictsPanel';
import { useICalFeedEventsBySource } from '../hooks/useICalFeed';
import { formatDate } from '../utils/formatDate';
import { calculateNights } from '../utils/calculateNights';
import { buildAvailabilityMap } from '../utils/availabilityMap';
import { buildAvailabilityGapMap } from '../utils/availabilityGaps';
import { Availability, AvailabilityDaySourceAttribution } from '../types/availability';

const EMPTY_DAY_SOURCE_ATTRIBUTION: ReadonlyMap<string, AvailabilityDaySourceAttribution> = new Map();

interface AvailabilityFormData {
  startDate: string;
  endDate: string;
  pricePerNight: number;
  notes?: string;
}

export const Availabilities = () => {
  const { apartmentId } = useParams<{ apartmentId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation(['common', 'pages', 'errors', 'forms', 'badges', 'navigation']);

  const navigationState = location.state as
    | { viewMode?: 'list' | 'calendar' | 'conflicts'; prefillStartDate?: string }
    | null
    | undefined;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAvailability, setEditingAvailability] = useState<Availability | null>(null);
  const [viewMode, setViewMode] = useState<'list' | 'calendar' | 'conflicts'>(
    navigationState?.viewMode ?? 'list'
  );
  const [prefillStartDate, setPrefillStartDate] = useState<string | null>(
    navigationState?.prefillStartDate ?? null
  );

  // Re-sync view mode / prefill date from navigation state on every navigation.
  // The useState initializers above only run on first mount, so navigating here
  // again while already on this route (e.g. clicking "Go to availability calendar"
  // from the Conflicts tab) would otherwise leave viewMode/prefillStartDate stale
  // since React Router re-renders in place rather than remounting the component.
  // location.key is unique per navigation entry, making it a reliable effect dependency.
  useEffect(() => {
    if (navigationState?.viewMode) {
      setViewMode(navigationState.viewMode);
    }
    if (navigationState?.prefillStartDate) {
      setPrefillStartDate(navigationState.prefillStartDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  // ID of the availability whose times form is currently expanded
  const [timesFormAvailId, setTimesFormAvailId] = useState<string | null>(null);

  // ── Confirm dialog state ───────────────────────────────────────────────────
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    message: string;
    onConfirm: () => void;
  }>({ open: false, message: '', onConfirm: () => void 0 });

  const { data: apartments } = useApartments();
  const { data: availabilities, isLoading } = useAvailabilities(apartmentId!);
  const { data: icalFeedEvents = [] } = useICalFeedEventsBySource(apartmentId ?? '', {
    includeDisappeared: false,
  });
  const addMutation = useAddAvailability(apartmentId!);
  const updateMutation = useUpdateAvailability(apartmentId!);
  const removeMutation = useRemoveAvailability(apartmentId!);
  const blockMutation = useBlockAvailability(apartmentId!);
  const unblockMutation = useUnblockAvailability(apartmentId!);
  const updateTimesMutation = useUpdateAvailabilityTimes(apartmentId!);

  const apartment = apartments?.find((a) => a.id === apartmentId);
  const defaultPrice = apartment?.details.pricePerNight || 100;

  const isMutating =
    blockMutation.isPending ||
    unblockMutation.isPending ||
    updateMutation.isPending ||
    removeMutation.isPending ||
    addMutation.isPending ||
    updateTimesMutation.isPending;

  const sortedAvailabilities = useMemo(
    () =>
      [...(availabilities ?? [])].sort(
        (a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime(),
      ),
    [availabilities],
  );

  const availabilityMap = useMemo(
    () => buildAvailabilityMap(sortedAvailabilities),
    [sortedAvailabilities],
  );

  const availabilityGapMap = useMemo(
    () => buildAvailabilityGapMap(sortedAvailabilities),
    [sortedAvailabilities],
  );

  const daySourceAttribution = useMemo<ReadonlyMap<string, AvailabilityDaySourceAttribution>>(() => {
    if (!apartmentId || icalFeedEvents.length === 0) {
      return EMPTY_DAY_SOURCE_ATTRIBUTION;
    }

    const attributionByDate = new Map<string, AvailabilityDaySourceAttribution>();

    for (const event of icalFeedEvents) {
      if (!event.startDate || !event.endDate) {
        continue;
      }

      const [sy, sm, sd] = event.startDate.split('-').map(Number);
      const [ey, em, ed] = event.endDate.split('-').map(Number);
      const start = new Date(sy, sm - 1, sd);
      const end = new Date(ey, em - 1, ed);
      const cursor = new Date(start);

      while (cursor <= end) {
        const y = cursor.getFullYear();
        const m = String(cursor.getMonth() + 1).padStart(2, '0');
        const d = String(cursor.getDate()).padStart(2, '0');
        const date = `${y}-${m}-${d}`;

        const current = attributionByDate.get(date) ?? {
          date,
          apartmentId,
          isDirectAvailability: false,
          icalFeedSources: [],
        };

        let nextSources = current.icalFeedSources;

        if (event.sourceKey && event.icalFeedConfigurationId) {
          const hasSource = current.icalFeedSources.some(
            source =>
              source.sourceKey === event.sourceKey &&
              source.icalFeedConfigurationId === event.icalFeedConfigurationId
          );

          if (!hasSource) {
            nextSources = [
              ...current.icalFeedSources,
              {
                sourceKey: event.sourceKey,
                sourceLabel: event.sourceLabel ?? event.sourceKey,
                icalFeedConfigurationId: event.icalFeedConfigurationId,
              },
            ];
          }
        }

        attributionByDate.set(date, {
          ...current,
          icalFeedSources: nextSources,
        });
        cursor.setDate(cursor.getDate() + 1);
      }
    }

    return attributionByDate;
  }, [apartmentId, icalFeedEvents]);

  const handleAdd = () => {
    setEditingAvailability(null);
    setIsModalOpen(true);
  };

  const handleAddFromDay = (isoDate: string) => {
    setEditingAvailability(null);
    setPrefillStartDate(isoDate);
    setIsModalOpen(true);
  };

  const handleEdit = (availability: Availability) => {
    setEditingAvailability(availability);
    setIsModalOpen(true);
  };

  const handleSubmit = async (data: AvailabilityFormData) => {
    try {
      if (editingAvailability) {
        await updateMutation.mutateAsync({
          availabilityId: editingAvailability.id,
          data: {
            startDate: data.startDate,
            endDate: data.endDate,
            pricePerNight: data.pricePerNight,
            notes: data.notes,
          },
        });
      } else {
        await addMutation.mutateAsync({
          startDate: data.startDate,
          endDate: data.endDate,
          pricePerNight: data.pricePerNight,
          notes: data.notes,
        });
      }
      setIsModalOpen(false);
    } catch (error) {
      console.error('Failed to save availability:', error);
    }
  };

  const handleRemove = (id: string) => {
    setConfirmDialog({
      open: true,
      message: t('pages:deleteAvailabilityConfirm'),
      onConfirm: async () => {
        setConfirmDialog(d => ({ ...d, open: false }));
        await removeMutation.mutateAsync(id);
      },
    });
  };

  const handleBlock = async (id: string) => {
    try {
      await blockMutation.mutateAsync(id);
    } catch (error) {
      console.error('Failed to block availability:', error);
    }
  };

  const handleUnblock = async (id: string) => {
    try {
      await unblockMutation.mutateAsync(id);
    } catch (error) {
      console.error('Failed to unblock availability:', error);
    }
  };

  const handleUpdateTimes = async (
    availabilityId: string,
    data: Parameters<typeof updateTimesMutation.mutateAsync>[0]['data']
  ) => {
    try {
      await updateTimesMutation.mutateAsync({ availabilityId, data });
      setTimesFormAvailId(null);
    } catch (error) {
      console.error('Failed to update availability times:', error);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'AVAILABLE':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'BLOCKED':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'BOOKED':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'AVAILABLE':
        return <Calendar size={16} className="text-green-600" />;
      case 'BLOCKED':
        return <Lock size={16} className="text-red-600" />;
      case 'BOOKED':
        return <Calendar size={16} className="text-gray-600" />;
      default:
        return <Calendar size={16} />;
    }
  };

  if (!apartmentId) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <AlertCircle size={48} className="text-red-500 mx-auto mb-4" />
          <p className="text-gray-600">{t('errors:noApartmentSelected')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(`/apartments/${apartmentId}`)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('pages:manageAvailability')}</h1>
            <p className="text-gray-600">{apartment?.name ?? t('common:loading')}</p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* View Toggle */}
          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('list')}
              className={`px-4 py-2 rounded-md transition-colors ${
                viewMode === 'list'
                  ? 'bg-white text-primary-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {t('pages:listView')}
            </button>
            <button
              onClick={() => setViewMode('calendar')}
              className={`px-4 py-2 rounded-md transition-colors ${
                viewMode === 'calendar'
                  ? 'bg-white text-primary-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {t('pages:calendarView')}
            </button>
            <button
              onClick={() => setViewMode('conflicts')}
              className={`px-4 py-2 rounded-md transition-colors ${
                viewMode === 'conflicts'
                  ? 'bg-white text-primary-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {t('pages:conflictsTab')}
            </button>
          </div>

          <button
            onClick={handleAdd}
            className="btn-primary flex items-center gap-2"
          >
            <Plus size={20} />
            {t('pages:addAvailability')}
          </button>
        </div>
      </div>

      {/* Stats */}
      {availabilities && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">{t('pages:totalPeriods')}</p>
                <p className="text-2xl font-bold text-gray-900">{availabilities.length}</p>
              </div>
              <Calendar size={32} className="text-primary-500" />
            </div>
          </div>

          <div className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">{t('pages:available')}</p>
                <p className="text-2xl font-bold text-green-600">
                  {availabilities.filter((a) => a.status === 'AVAILABLE').length}
                </p>
              </div>
              <Unlock size={32} className="text-green-500" />
            </div>
          </div>

          <div className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">{t('pages:blocked')}</p>
                <p className="text-2xl font-bold text-red-600">
                  {availabilities.filter((a) => a.status === 'BLOCKED').length}
                </p>
              </div>
              <Lock size={32} className="text-red-500" />
            </div>
          </div>

          <div className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">{t('pages:booked')}</p>
                <p className="text-2xl font-bold text-gray-600">
                  {availabilities.filter((a) => a.status === 'BOOKED').length}
                </p>
              </div>
              <Calendar size={32} className="text-gray-500" />
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      {viewMode === 'conflicts' ? (
        <ConflictsPanel apartmentId={apartmentId} />
      ) : isLoading ? (
        <div className="card">
          <p className="text-gray-600 text-center py-12">{t('pages:loadingAvailabilities')}</p>
        </div>
      ) : !availabilities || availabilities.length === 0 ? (
        <div className="card">
          <div className="text-center py-12">
            <Calendar size={48} className="text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 mb-4">{t('pages:noAvailabilityPeriods')}</p>
            <button onClick={handleAdd} className="btn-primary">
              {t('pages:addFirstAvailability')}
            </button>
          </div>
        </div>
      ) : viewMode === 'list' ? (
        <div className="space-y-4">
          {availabilities
            .sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime())
            .map((availability) => (
              <div
                key={availability.id}
                className={`card hover:shadow-lg transition-shadow border-2 ${
                  availability.status === 'BOOKED' ? 'opacity-75' : ''
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <span
                        className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(
                          availability.status
                        )}`}
                      >
                        {getStatusIcon(availability.status)}
                        {availability.status}
                      </span>

                      {availability.status === 'BOOKED' && availability.bookedByBookingId && (
                        <span className="text-sm text-gray-600">
                          {t('pages:bookingRef', { id: availability.bookedByBookingId.substring(0, 8) })}
                        </span>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <p className="text-sm text-gray-600 mb-1">{t('pages:fieldDateRange')}</p>
                        <p className="font-medium text-gray-900">
                          {formatDate(availability.startDate)} → {formatDate(availability.endDate)}
                        </p>
                        <p className="text-sm text-gray-600 mt-1">
                          {t('common:nights_other', { count: calculateNights(availability.startDate, availability.endDate) })}
                        </p>
                      </div>

                      <div>
                        <p className="text-sm text-gray-600 mb-1">{t('pages:fieldPricePerNight')}</p>
                        <p className="font-medium text-gray-900 flex items-center gap-1">
                          <DollarSign size={16} className="text-accent-500" />
                          €{availability.pricePerNight}
                        </p>
                        <p className="text-sm text-gray-600 mt-1">
                          {t('pages:fieldTotal')} €
                          {availability.pricePerNight *
                            calculateNights(availability.startDate, availability.endDate)}
                        </p>
                      </div>

                      {availability.notes && (
                        <div>
                          <p className="text-sm text-gray-600 mb-1">{t('forms:notes')}</p>
                          <p className="text-sm text-gray-700">{availability.notes}</p>
                        </div>
                      )}

                      {/* Check-in/out times summary */}
                      {(availability.checkInTime || availability.checkOutTime) && (
                        <div>
                          <p className="text-sm text-gray-600 mb-1">
                            {t('forms:checkInTime')} / {t('forms:checkOutTime')}
                            <span className="ml-1 text-xs text-gray-400">
                              {t('pages:utcLabel')}
                            </span>
                          </p>
                          <p className="text-sm text-gray-700 flex items-center gap-2">
                            <Clock size={14} className="text-gray-400" />
                            {availability.checkInTime ?? '—'}{' '}
                            →{' '}
                            {availability.checkOutTime ?? '—'}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2 ml-4">
                    {/* Times edit button — available on all non-BOOKED rows */}
                    {availability.status !== 'BOOKED' && (
                      <button
                        onClick={() =>
                          setTimesFormAvailId(
                            timesFormAvailId === availability.id ? null : availability.id
                          )
                        }
                        className={`p-2 hover:bg-blue-50 rounded-lg transition-colors ${
                          timesFormAvailId === availability.id ? 'bg-blue-50' : ''
                        }`}
                        title={t('forms:checkInTime') + ' / ' + t('forms:checkOutTime')}
                        aria-pressed={timesFormAvailId === availability.id}
                      >
                        <Clock
                          size={18}
                          className={
                            timesFormAvailId === availability.id
                              ? 'text-blue-600'
                              : 'text-gray-600'
                          }
                        />
                      </button>
                    )}

                    {availability.status !== 'BOOKED' && (
                      <>
                        <button
                          onClick={() => handleEdit(availability)}
                          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                          title={t('pages:editAvailability')}
                        >
                          <Edit2 size={18} className="text-gray-600" />
                        </button>

                        {availability.status === 'AVAILABLE' ? (
                          <button
                            onClick={() => handleBlock(availability.id)}
                            className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                            title={t('pages:blockDates')}
                            disabled={blockMutation.isPending}
                          >
                            <Lock size={18} className="text-red-600" />
                          </button>
                        ) : (
                          <button
                            onClick={() => handleUnblock(availability.id)}
                            className="p-2 hover:bg-green-50 rounded-lg transition-colors"
                            title={t('pages:unblockDates')}
                            disabled={unblockMutation.isPending}
                          >
                            <Unlock size={18} className="text-green-600" />
                          </button>
                        )}

                        <button
                          onClick={() => handleRemove(availability.id)}
                          className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                          title={t('common:delete')}
                          disabled={removeMutation.isPending}
                        >
                          <Trash2 size={18} className="text-red-600" />
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {/* Inline times form — shown when the clock icon is toggled */}
                {timesFormAvailId === availability.id && (
                  <AvailabilityTimesForm
                    availability={availability}
                    onSubmit={(data) => handleUpdateTimes(availability.id, data)}
                    onCancel={() => setTimesFormAvailId(null)}
                    isLoading={updateTimesMutation.isPending}
                  />
                )}
              </div>
            ))}
        </div>
      ) : (
        <AvailabilityCalendarView
          availabilities={availabilities ?? []}
          availabilityMap={availabilityMap}
          availabilityGapMap={availabilityGapMap}
          defaultPrice={defaultPrice}
          daySourceAttribution={daySourceAttribution}
          onEdit={handleEdit}
          onBlock={handleBlock}
          onUnblock={handleUnblock}
          onRemove={handleRemove}
          onAddFromDay={handleAddFromDay}
          isMutating={isMutating}
        />
      )}

      {/* Add/Edit Modal */}
      {isModalOpen && (
        <AvailabilityModal
          availability={editingAvailability}
          defaultPrice={defaultPrice}
          initialStartDate={prefillStartDate ?? undefined}
          onClose={() => {
            setIsModalOpen(false);
            setPrefillStartDate(null);
          }}
          onSubmit={handleSubmit}
          isLoading={addMutation.isPending || updateMutation.isPending}
        />
      )}

      {/* Confirm dialog */}
      <ConfirmDialog
        open={confirmDialog.open}
        message={confirmDialog.message}
        intent="danger"
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog(d => ({ ...d, open: false }))}
      />
    </div>
  );
};

// Modal Component
interface AvailabilityModalProps {
  availability: Availability | null;
  defaultPrice: number;
  initialStartDate?: string;
  onClose: () => void;
  onSubmit: (data: AvailabilityFormData) => void;
  isLoading: boolean;
}

const AvailabilityModal = ({
  availability,
  defaultPrice,
  initialStartDate,
  onClose,
  onSubmit,
  isLoading,
}: AvailabilityModalProps) => {
  const { t } = useTranslation(['common', 'pages', 'errors', 'forms', 'badges', 'navigation']);
  const [formData, setFormData] = useState<AvailabilityFormData>({
    startDate: availability?.startDate || (availability === null && initialStartDate ? initialStartDate : ''),
    endDate: availability?.endDate || '',
    pricePerNight: availability?.pricePerNight || defaultPrice,
    notes: availability?.notes || '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const calculateNightsInModal = () => {
    if (!formData.startDate || !formData.endDate) return 0;
    return calculateNights(formData.startDate, formData.endDate);
  };

  const totalPrice = formData.pricePerNight * calculateNightsInModal();

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
        <div className="px-6 py-4 border-b flex justify-between items-center">
          <h2 className="text-xl font-bold text-gray-900">
            {availability ? t('pages:editAvailability') : t('pages:addAvailability')}
          </h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <Plus size={24} className="rotate-45 text-gray-600" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('forms:startDate')}
            </label>
            <input
              type="date"
              value={formData.startDate}
              onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
              className="input"
              required
              min={new Date().toISOString().split('T')[0]}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('forms:endDate')}
            </label>
            <input
              type="date"
              value={formData.endDate}
              onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
              className="input"
              required
              min={formData.startDate || new Date().toISOString().split('T')[0]}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('forms:pricePerNight')}
            </label>
            <input
              type="number"
              value={formData.pricePerNight}
              onChange={(e) =>
                setFormData({ ...formData, pricePerNight: parseFloat(e.target.value) })
              }
              className="input"
              required
              min="0"
              step="0.01"
            />
          </div>

          {calculateNightsInModal() > 0 && (
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600">
                {t('pages:availabilityCalculation', {
                  nights: calculateNightsInModal(),
                  price: formData.pricePerNight,
                  total: totalPrice.toFixed(2),
                })}
              </p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('forms:notes')}
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="input"
              rows={3}
              placeholder={t('forms:notesPlaceholder')}
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              {t('common:cancel')}
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 btn-primary"
            >
              {isLoading
                ? t('common:saving')
                : availability
                ? t('pages:updateAvailability')
                : t('pages:addAvailabilitySubmit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
